import { toCurrency } from '@/utils';
import { toFormattedDate } from '@/utils/date';

export const SALARY_SETTLEMENT_FILTER_KEYS = {
  STARTS_ON: 'starts_on',
  ENDS_ON: 'ends_on',
  SALARY_OR_HOURLY: 'salary_or_hourly',
  PAY_SCHEDULE: 'pay_schedule_id',
  DIVISION: 'division_id',
  BANKING_PROVIDER: 'banking_provider',
  PAYMENT_RAIL: 'payment_rail',
};

export const SALARY_SETTLEMENT_API_FILTER_KEYS = [
  SALARY_SETTLEMENT_FILTER_KEYS.STARTS_ON,
  SALARY_SETTLEMENT_FILTER_KEYS.ENDS_ON,
];

export const SALARY_SETTLEMENT_SEARCH_FIELDS = ['employee_code', 'full_name', 'work_email'];

export const SALARY_SETTLEMENT_FIELD_KEYS = {
  EMPLOYEE_CODE: 'employee_code',
  FULL_NAME: 'full_name',
  WORK_EMAIL: 'work_email',
  SALARY_COMPLETED_AT: 'salary_completed_at',
  SALARY_QUEUED_AT: 'salary_queued_at',
  BANKING_PROVIDER: 'banking_provider',
  PAYMENT_RAIL: 'payment_rail',
  AMOUNT_SETTLED: 'amount_settled',
  TOTAL_TRANSFERRED: 'total_transferred',
  SALARY_AMOUNT: 'salary_amount',
  SETTLEMENT_AMOUNT: 'settlement_amount',
  ENROLLED_DATE: 'enrolled_date',
  DISABLED_DATE: 'disabled_date',
  WAS_ENROLLED_THIS_PERIOD: 'was_enrolled_this_period',
  WAS_DISABLED_THIS_PERIOD: 'was_disabled_this_period',
  REPORT_START_DATE: 'report_start_date',
  REPORT_END_DATE: 'report_end_date',
};

export const SALARY_SETTLEMENT_FIELD_DEFINITIONS = [
  {
    key: SALARY_SETTLEMENT_FIELD_KEYS.FULL_NAME,
    label: 'Employee Name',
    sortable: true,
  },
  {
    key: SALARY_SETTLEMENT_FIELD_KEYS.WORK_EMAIL,
    label: 'Email',
    sortable: true,
  },
  {
    key: SALARY_SETTLEMENT_FIELD_KEYS.SALARY_COMPLETED_AT,
    label: 'Salary Completed',
    sortable: true,
    formatter: value => (value ? toFormattedDate(value, 'DD/MM/YYYY HH:mm') : '-'),
  },
  {
    key: SALARY_SETTLEMENT_FIELD_KEYS.SALARY_AMOUNT,
    label: 'Salary Amount',
    sortable: true,
    formatter: value => toCurrency(value || 0),
    tdClass: 'text-right',
    thClass: 'text-right',
  },
  {
    key: SALARY_SETTLEMENT_FIELD_KEYS.AMOUNT_SETTLED,
    label: 'Amount Settled',
    sortable: true,
    formatter: value => toCurrency(value || 0),
    tdClass: 'text-right',
    thClass: 'text-right',
  },
  {
    key: SALARY_SETTLEMENT_FIELD_KEYS.TOTAL_TRANSFERRED,
    label: 'Total Transferred',
    sortable: true,
    formatter: value => toCurrency(value || 0),
    tdClass: 'text-right',
    thClass: 'text-right',
  },
  {
    key: SALARY_SETTLEMENT_FIELD_KEYS.ENROLLED_DATE,
    label: 'Enrolled Date',
    sortable: true,
    formatter: value => (value ? toFormattedDate(value, 'DD/MM/YYYY HH:mm') : '-'),
  },
  {
    key: SALARY_SETTLEMENT_FIELD_KEYS.DISABLED_DATE,
    label: 'Disabled Date',
    sortable: true,
    formatter: value => (value ? toFormattedDate(value, 'DD/MM/YYYY HH:mm') : '-'),
  },
  {
    key: SALARY_SETTLEMENT_FIELD_KEYS.EMPLOYEE_CODE,
    label: 'Employee Code',
    sortable: true,
    formatter: value => value || '-',
  },
  {
    key: SALARY_SETTLEMENT_FIELD_KEYS.SALARY_QUEUED_AT,
    label: 'Salary Queued',
    sortable: true,
    formatter: value => (value ? toFormattedDate(value, 'DD/MM/YYYY HH:mm') : '-'),
  },
  // Left as raw enums rather than collapsed through formatPaymentMethod — the SOX report should match its source.
  {
    key: SALARY_SETTLEMENT_FIELD_KEYS.BANKING_PROVIDER,
    label: 'Banking Provider',
    sortable: true,
    formatter: value => value || '-',
  },
  {
    key: SALARY_SETTLEMENT_FIELD_KEYS.PAYMENT_RAIL,
    label: 'Payment Rail',
    sortable: true,
    formatter: value => value || '-',
  },
  {
    key: SALARY_SETTLEMENT_FIELD_KEYS.SETTLEMENT_AMOUNT,
    label: 'Settlement Amount',
    sortable: true,
    formatter: value => toCurrency(value || 0),
    tdClass: 'text-right',
    thClass: 'text-right',
  },
  // Missing renders as '-' rather than 'No' — on an audit report, absent data isn't a negative finding.
  {
    key: SALARY_SETTLEMENT_FIELD_KEYS.WAS_ENROLLED_THIS_PERIOD,
    label: 'Enrolled This Period',
    sortable: true,
    formatter: value => (value == null ? '-' : value ? 'Yes' : 'No'),
  },
  {
    key: SALARY_SETTLEMENT_FIELD_KEYS.WAS_DISABLED_THIS_PERIOD,
    label: 'Disabled This Period',
    sortable: true,
    formatter: value => (value == null ? '-' : value ? 'Yes' : 'No'),
  },
  // Date-only (the endpoint returns ::date): these stamp the period into the export so a downloaded
  // file says which window it covers, which the filename alone can't survive Excel's 31-char cap.
  {
    key: SALARY_SETTLEMENT_FIELD_KEYS.REPORT_START_DATE,
    label: 'Report Start Date',
    sortable: true,
    formatter: value => (value ? toFormattedDate(value, 'DD/MM/YYYY') : '-'),
  },
  {
    key: SALARY_SETTLEMENT_FIELD_KEYS.REPORT_END_DATE,
    label: 'Report End Date',
    sortable: true,
    formatter: value => (value ? toFormattedDate(value, 'DD/MM/YYYY') : '-'),
  },
];

// The standalone /salary-settlement page's columns — deliberately a subset of the definitions above.
export const SALARY_SETTLEMENT_DEFAULT_FIELD_KEYS = [
  SALARY_SETTLEMENT_FIELD_KEYS.FULL_NAME,
  SALARY_SETTLEMENT_FIELD_KEYS.WORK_EMAIL,
  SALARY_SETTLEMENT_FIELD_KEYS.SALARY_COMPLETED_AT,
  SALARY_SETTLEMENT_FIELD_KEYS.SALARY_AMOUNT,
  SALARY_SETTLEMENT_FIELD_KEYS.AMOUNT_SETTLED,
  SALARY_SETTLEMENT_FIELD_KEYS.TOTAL_TRANSFERRED,
  SALARY_SETTLEMENT_FIELD_KEYS.ENROLLED_DATE,
  SALARY_SETTLEMENT_FIELD_KEYS.DISABLED_DATE,
];

// Every renderable column, ordered to mirror the SELECT in /reports/employees-salary-settlement.
export const SOX_REPORT_FIELD_KEYS = [
  SALARY_SETTLEMENT_FIELD_KEYS.EMPLOYEE_CODE,
  SALARY_SETTLEMENT_FIELD_KEYS.FULL_NAME,
  SALARY_SETTLEMENT_FIELD_KEYS.WORK_EMAIL,
  SALARY_SETTLEMENT_FIELD_KEYS.SALARY_COMPLETED_AT,
  SALARY_SETTLEMENT_FIELD_KEYS.SALARY_QUEUED_AT,
  SALARY_SETTLEMENT_FIELD_KEYS.BANKING_PROVIDER,
  SALARY_SETTLEMENT_FIELD_KEYS.PAYMENT_RAIL,
  SALARY_SETTLEMENT_FIELD_KEYS.AMOUNT_SETTLED,
  SALARY_SETTLEMENT_FIELD_KEYS.TOTAL_TRANSFERRED,
  SALARY_SETTLEMENT_FIELD_KEYS.SALARY_AMOUNT,
  SALARY_SETTLEMENT_FIELD_KEYS.SETTLEMENT_AMOUNT,
  SALARY_SETTLEMENT_FIELD_KEYS.ENROLLED_DATE,
  SALARY_SETTLEMENT_FIELD_KEYS.DISABLED_DATE,
  SALARY_SETTLEMENT_FIELD_KEYS.WAS_ENROLLED_THIS_PERIOD,
  SALARY_SETTLEMENT_FIELD_KEYS.WAS_DISABLED_THIS_PERIOD,
  SALARY_SETTLEMENT_FIELD_KEYS.REPORT_START_DATE,
  SALARY_SETTLEMENT_FIELD_KEYS.REPORT_END_DATE,
];

// Resolves in `fieldKeys` order, not definition order, so callers control column order.
export const resolveFieldDefinitions = (fieldKeys = SALARY_SETTLEMENT_DEFAULT_FIELD_KEYS) =>
  (fieldKeys ?? SALARY_SETTLEMENT_DEFAULT_FIELD_KEYS)
    .map(key => SALARY_SETTLEMENT_FIELD_DEFINITIONS.find(field => field.key === key))
    .filter(Boolean);

export const buildExportHeaders = fieldDefinitions => fieldDefinitions.map(field => field.label);

// Dates compacted to YYYYMMDD so 'sox-report-20260701-20260731' (28 chars) survives ExportButton's
// 30-char truncation for XLSX, which would otherwise eat the end date.
export const buildExportFileName = (baseName, startsOn, endsOn) => {
  const compact = date => (date ? String(date).replace(/-/g, '') : '');
  const range = [compact(startsOn), compact(endsOn)].filter(Boolean).join('-');

  return range ? `${baseName}-${range}` : baseName;
};

export const buildExportRow = (fieldDefinitions, row) =>
  fieldDefinitions.map(field => (field.formatter ? field.formatter(row[field.key]) : row[field.key]));

export const SALARY_SETTLEMENT_PER_PAGE_OPTIONS = [25, 50, 100, 250];
